import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertTriangle, Trash2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const DeleteAccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  if (!user) {
    navigate("/connexion");
    return null;
  }

  const handleDeleteAccount = async () => {
    if (confirmText.toUpperCase() !== "SUPPRIMER") {
      toast.error("Veuillez taper 'SUPPRIMER' pour confirmer");
      return;
    }

    setLoading(true);
    try {
      // 1. Supprimer toutes les données utilisateur via la fonction Supabase
      const { data, error: dataError } = await supabase.rpc('delete_user_data', {
        p_user_id: user.id
      });

      if (dataError) {
        console.error("Error deleting user data:", dataError);
        throw dataError;
      }

      // 2. Supprimer le compte utilisateur de Supabase Auth
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id);
      
      if (authError) {
        // Si l'API admin ne fonctionne pas (normal côté client), on déconnecte quand même
        console.warn("Could not delete auth user (admin API needed):", authError);
      }

      // 3. Déconnexion
      await logout();

      toast.success("Compte supprimé", {
        description: "Toutes vos données ont été effacées définitivement."
      });

      // Rediriger vers la page d'accueil après 2 secondes
      setTimeout(() => {
        navigate("/");
      }, 2000);

    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Erreur", {
        description: "Une erreur s'est produite lors de la suppression."
      });
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl px-4 py-12">
      <Card className="border-red-200 shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="bg-red-50 dark:bg-red-950/20 p-4 rounded-full">
              <AlertTriangle className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl text-ink-primary">
            Supprimer mon compte
          </CardTitle>
          <CardDescription className="font-body text-ink-secondary">
            Effacer définitivement toutes mes données
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Informations */}
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 dark:text-amber-100 font-body space-y-2">
                <p className="font-semibold">Cette action est irréversible !</p>
                <p>Les données suivantes seront supprimées définitivement :</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Toutes vos notes</li>
                  <li>Tous vos surlignages</li>
                  <li>Tous vos garde-pages</li>
                  <li>Votre statut Premium</li>
                  <li>Votre compte et vos informations personnelles</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Compte connecté */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-xs text-ink-secondary">Compte connecté</Label>
            <p className="text-sm font-body text-ink-primary mt-1">{user.email}</p>
          </div>

          {/* Bouton de suppression */}
          <div className="pt-4">
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={loading}
              variant="destructive"
              size="lg"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-body py-6"
            >
              <Trash2 className="h-5 w-5 mr-2" />
              Supprimer mon compte et mes données
            </Button>
          </div>

          {/* Lien retour */}
          <div className="text-center pt-4 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-ink-secondary hover:text-ink-primary"
            >
              Annuler et retourner à l'accueil
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de confirmation */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmation de suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Vous êtes sur le point de supprimer définitivement votre compte et toutes vos données.
              </p>
              <p className="font-semibold text-ink-primary">
                Cette action est IRRÉVERSIBLE. Toutes vos données seront perdues.
              </p>
              <div className="pt-2">
                <Label htmlFor="confirm-delete" className="text-ink-primary">
                  Tapez <span className="font-bold">SUPPRIMER</span> pour confirmer :
                </Label>
                <Input
                  id="confirm-delete"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="mt-2 uppercase text-center font-mono"
                  disabled={loading}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={loading || confirmText.toUpperCase() !== "SUPPRIMER"}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeleteAccountPage;
